import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Skeleton } from '@/ui/Skeleton';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { EstadoError } from '@/components/EstadoError';
import { useCumplimiento, type CertificadoPorVencer } from './api';

/**
 * Cumplimiento.tsx — cuánta gente terminó los cursos obligatorios, y qué
 * certificados están por vencer.
 *
 * ## Lo que se midió aquí
 *
 * El vanilla pintaba cada barra con un color elegido por el porcentaje
 * —`>=80` verde, `>=50` naranja, el resto rojo— escrito tres veces con
 * `var(--green-text)` y compañía. El color por sí solo no dice nada a quien no
 * lo distingue: ahora el número va **siempre escrito** («18 de 24 · 75 %») y el
 * color acompaña, no informa.
 *
 * El panel tampoco tenía estado de error con reintento: al fallar pintaba un
 * `alert-error` con el mensaje y ahí se quedaba, con el panel abierto y sin
 * forma de volver a intentarlo.
 */

const SEMAFORO: Record<CertificadoPorVencer['status'], { tono: 'ok' | 'warn' | 'danger'; texto: string }> = {
  vigente: { tono: 'ok', texto: 'Vigente' },
  por_vencer: { tono: 'warn', texto: 'Por vencer' },
  vencido: { tono: 'danger', texto: 'Vencido' },
};

export function Cumplimiento({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const { data, isLoading, isError, error, refetch } = useCumplimiento(abierto);

  return (
    <Drawer
      abierto={abierto}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Panel de cumplimiento"
      descripcion="Cursos obligatorios y certificados del equipo."
      pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
    >
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : isError ? (
        <EstadoError err={error} que="el cumplimiento" onReintentar={() => void refetch()} />
      ) : !data ? null : (
        <div className="flex flex-col gap-6">
          <section aria-labelledby="cump-obligatorios">
            <h3 id="cump-obligatorios" className="flex flex-wrap items-baseline gap-2 text-3 font-bold text-brand-ink">
              <span>
                <i className="bi bi-mortarboard" aria-hidden="true" /> Cursos obligatorios
              </span>
              <span className="text-2 font-normal text-muted">
                {data.total_collaborators} colaboradores
              </span>
            </h3>

            {!data.mandatory_compliance.length ? (
              <p className="mt-2 text-3 text-muted">
                No hay ningún curso marcado como obligatorio. Puedes marcarlo al crearlo.
              </p>
            ) : (
              <ul className="mt-3 flex flex-col gap-3">
                {data.mandatory_compliance.map((m) => {
                  const pct = Math.round(m.pct);
                  return (
                    <li key={m.title}>
                      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2 text-2">
                        <span className="min-w-0 flex-1 truncate text-ink">{m.title}</span>
                        {/* El número va escrito, no solo el color de la barra. */}
                        <strong
                          className={cn(
                            'tabular-nums',
                            pct >= 80 ? 'text-ok-ink' : pct >= 50 ? 'text-warn-ink' : 'text-danger-ink',
                          )}
                        >
                          {m.completed} de {m.total} · {pct} %
                        </strong>
                      </div>
                      <BarraDeProgreso pct={pct} etiqueta={`Cumplimiento de ${m.title}`} />
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section aria-labelledby="cump-certificados">
            <h3 id="cump-certificados" className="text-3 font-bold text-brand-ink">
              <i className="bi bi-patch-check" aria-hidden="true" /> Certificados ({data.cert_summary.total})
            </h3>

            <ul className="mt-3 grid grid-cols-3 gap-2">
              {(['vigente', 'por_vencer', 'vencido'] as const).map((k) => (
                <li
                  key={k}
                  className="rounded-sm border border-line bg-surface p-2.5 text-center"
                >
                  <span
                    className={cn(
                      'block text-6 font-extrabold leading-none tabular-nums',
                      SEMAFORO[k].tono === 'ok' ? 'text-ok-ink'
                        : SEMAFORO[k].tono === 'warn' ? 'text-warn-ink' : 'text-danger-ink',
                    )}
                  >
                    {data.cert_summary[k] || 0}
                  </span>
                  <span className="mt-1 block text-1 text-muted">{SEMAFORO[k].texto}s</span>
                </li>
              ))}
            </ul>

            {!data.expiring.length ? (
              <p className="mt-3 flex items-center gap-2 rounded-sm border border-line bg-sunken p-2.5 text-2 text-ink-2">
                <i className="bi bi-check-circle text-ok-ink" aria-hidden="true" />
                Ningún certificado está por vencer.
              </p>
            ) : (
              <>
                <h4 className="mb-2 mt-4 text-2 font-semibold text-ink-2">
                  Por vencer y vencidos
                </h4>
                <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto">
                  {data.expiring.map((e, i) => {
                    const sem = SEMAFORO[e.status] ?? SEMAFORO.por_vencer;
                    return (
                      <li
                        key={`${e.full_name}-${e.course_title}-${i}`}
                        className="flex items-center gap-2.5 rounded-sm border border-line bg-sunken p-2.5"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-2 font-semibold text-ink">{e.full_name}</p>
                          <p className="truncate text-1 text-muted">
                            {e.area} · {e.course_title}
                          </p>
                        </div>
                        <span className="shrink-0 text-right">
                          <Badge tone={sem.tono}>{sem.texto}</Badge>
                          {e.expires_at && (
                            <span className="mt-0.5 block text-1 text-muted">
                              {Utils.fmtDate(e.expires_at)}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}
